import React, { useEffect, useState } from 'react';
import { apiFetchAuthed } from '../../apiHelpers';
import { REASONS } from './FlerpieFeedback';

/** The debug dashboard's list of verdicts, newest first. Admin only at the API. */

type Row = {
  id: string;
  verdict: 'up' | 'down';
  reason: string | null;
  note: string | null;
  created_at: string;
  user_name: string | null;
  question: string | null;
  answer: string;
  sql_generated: string | null;
};

const reasonLabel = (key: string | null) => REASONS.find((r) => r.key === key)?.label || key || '';

const FlerpieFeedbackPanel: React.FC = () => {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiFetchAuthed('/ai/feedback?limit=100')
      .then((data: Row[]) => { if (alive) setRows(data || []); })
      .catch((err: Error) => { if (alive) setError(err.message); });
    return () => { alive = false; };
  }, []);

  if (error) return <div style={{ padding: 24, color: '#ff6b6b' }}>Could not load feedback: {error}</div>;
  if (!rows) return <div style={{ padding: 24, color: '#666' }}>Loading feedback...</div>;
  if (rows.length === 0) return <div style={{ padding: 24, color: '#666' }}>No verdicts yet. They appear here as people rate answers.</div>;

  const ups = rows.filter((r) => r.verdict === 'up').length;

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 960 }}>
      <div style={{ fontSize: 12, color: '#888' }}>
        {rows.length} verdicts, {ups} helpful, {rows.length - ups} not.
      </div>
      {rows.map((r) => (
        <div key={r.id} style={{ background: '#1a1a1a', borderRadius: 12, padding: 14, borderLeft: `3px solid ${r.verdict === 'up' ? '#5ec269' : '#d65c5c'}` }}>
          <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#888', flexWrap: 'wrap' }}>
            <span style={{ color: r.verdict === 'up' ? '#5ec269' : '#d65c5c', fontWeight: 700 }}>{r.verdict === 'up' ? 'Helpful' : 'Not helpful'}</span>
            {r.reason && <span style={{ color: '#FFCC33' }}>{reasonLabel(r.reason)}</span>}
            <span>{r.user_name || 'unknown'}</span>
            <span>{new Date(r.created_at).toLocaleString()}</span>
          </div>
          {r.note && <div style={{ marginTop: 6, fontSize: 12, color: '#ddd' }}>"{r.note}"</div>}
          {r.question && <div style={{ marginTop: 8, fontSize: 11, color: '#aaa' }}><span style={{ color: '#666' }}>Asked: </span>{r.question}</div>}
          <div style={{ marginTop: 4, fontSize: 11, color: '#aaa', whiteSpace: 'pre-wrap' }}><span style={{ color: '#666' }}>Flerpie: </span>{r.answer}</div>
          {r.sql_generated && (
            <pre style={{ marginTop: 8, fontSize: 10, color: '#777', background: 'rgba(0,0,0,0.3)', padding: 8, borderRadius: 6, overflowX: 'auto', maxHeight: 120 }}>{r.sql_generated}</pre>
          )}
        </div>
      ))}
    </div>
  );
};

export default FlerpieFeedbackPanel;
